import { Flex, Spinner } from '@chakra-ui/react';
import {
  createOAuthStateKeyPair,
  decryptForOAuthState,
  deleteOAuthStatePrivateKeyFromSessionStorage,
  saveOAuthStatePrivateKeyToSessionStorage,
} from '@halo/utils';
import { WalletCore } from '@halo/wallet-app';
import { useLayoutEffect } from 'react';
import { useNavigate } from 'react-router';
import { userApis } from '@/configs/apis';
import Paths from '@/configs/router/paths';
import { WalletCloud } from '@/scripts/walletCloud';
import { useSessionStore } from '@/store/session';
import { useWalletStore } from '@/wallet';
import { localUrl } from '@/configs/env';
import URLs from '@/scripts/request/URLs';
import { requestDeviceInfo } from '@/configs/firebase/config';
import { DebugForm } from '../main/NavBar/Nav';
import { checkUrlFromAccountCenter } from './helpers';

export default function LoginLoading() {
  const navigate = useNavigate();
  const url = new URL(location.href);
  const isDebug = url.searchParams.get('debug') === 'true';
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);

  useLayoutEffect(() => {
    (async () => {
      if (isDebug) {
        return;
      }

      const params = Object.fromEntries(url.searchParams.entries());
      const isFromAccountCenter = checkUrlFromAccountCenter();
      const redirect_uri = `${localUrl}${Paths.LOGIN}`;
      const referrer_url = localUrl;

      if (isFromAccountCenter) {
        const data = await userApis.getState({ state: params.state });
        const decrypted = await decryptForOAuthState(params.state, data.data);
        const authParams = JSON.parse(decrypted);
        await deleteOAuthStatePrivateKeyFromSessionStorage(params.state).catch(() => null);

        const {
          type,
          device_id,
          authInfo,
          cloudInfo,
          wallet,
          isSessionStorage = false,
        } = authParams;

        let walletInfo: any = null;
        let isReadOnly = false;

        switch (type) {
          case 'QRCode': {
            if (
              wallet?.accounts?.length &&
              (wallet.accounts.some((account: any) => 'seriesWallets' in account) ||
                wallet?.version === 1)
            ) {
              const newAccounts = WalletCore.migrateAccounts(wallet.accounts || []);
              wallet.accounts = newAccounts;
            }
            walletInfo = wallet;
            isReadOnly = true;
            break;
          }
          case 'Apple':
          case 'Google': {
            if (authInfo && cloudInfo) {
              const { ckWebAuthToken } = authInfo;
              const { platform, cid } = cloudInfo;
              const walletCloud = new WalletCloud({
                type: platform,
                key: import.meta.env.VITE_APP_FILE_NAME,
                clientParams: {} as any,
              });

              await walletCloud.init({
                cid,
                authInfo,
                clientParams: {
                  ckWebAuthToken,
                } as any,
              });

              walletInfo = await walletCloud.getCloudAccount();
            }

            break;
          }
        }

        const account = walletInfo.accounts.find(
          (account: any) => account?.isDefault && account?.uid
        );

        useWalletStore.init({
          readonly: isReadOnly,
          userId: account.uid,
          isChangedPassword: walletInfo.isChange,
          defaultPassword: walletInfo.password,
          accounts: walletInfo.accounts,
          updateTime: walletInfo.t,
        });

        userApis.setAuthParams(authParams);

        await useSessionStore.getState().login({
          code: params.code,
          scope: params.scope,
          state: params.state,
          redirect_uri,
          clientUid: account.clientUid,
          device_id,
          isSessionStorage,
        });
        requestDeviceInfo('', true);

        navigate(Paths.MESSAGES_CHATS);
      } else {
        const url = new URLs.AUTH('/v1/oauth/authorization');

        const loginUri = import.meta.env.VITE_AUTH_URL && `${import.meta.env.VITE_AUTH_URL}/auth`;
        if (loginUri) {
          url.searchParams.set('login_uri', loginUri);
        }

        url.searchParams.set('redirect_uri', redirect_uri);
        url.searchParams.set('referrer_url', referrer_url);

        const { state, privateJwk } = await createOAuthStateKeyPair();
        await saveOAuthStatePrivateKeyToSessionStorage(state, privateJwk);

        url.searchParams.set('state', state);

        location.href = url.toString();
      }
    })().catch(() => {
      navigate(Paths.LOGIN);
    });
  });

  return (
    <Flex justifyContent="center" alignItems="center" h="full" w="full">
      {isDebug ? (
        <div onClick={() => setIsSettingsDialogOpen(true)}>
          Open debug form
          <DebugForm
            open={isSettingsDialogOpen}
            onOpenChange={({ open }) => setIsSettingsDialogOpen(open)}
          ></DebugForm>
        </div>
      ) : (
        <Spinner />
      )}
    </Flex>
  );
}
